// @ts-nocheck
import { assign, spawn, sendTo, raise } from 'xstate';
import logger from '../../logger';
import { DryChromeMachine } from './dryChromeState';

/**
 * primeApplicationState — runs after fetchRequiredAppData completes.
 *
 * All required data (notes, notebooks, userSettings, AOF collections, chromeModels,
 * userPanelConfigs) is already in context when this state is entered. The goal is
 * to derive the UI-ready selections from that data as fast as possible.
 *
 * Design rules:
 * - NO async work here. Every action is a synchronous assign reading from context.
 * - NO polling loops. All "always" guards proceed as soon as their data invariant
 *   is satisfied; if data is missing they fall through to a safe default.
 * - Parallel children (primeDryChrome, primeAreaOfFocus, primeSelectedNotebook,
 *   primeNotes, primeNotebooks) all run concurrently. The parent reaches onDone
 *   only when every child reaches a final state.
 *
 * Ordering dependency: primeAreaOfFocus.setAreaOfFocus runs setSelectAreaOfFocus
 * (entry) before primeNotes.createNotesList runs retrySetSelectedNoteIfNeeded
 * (entry), because XState processes parallel state entries in definition order.
 * selectedAreaOfFocus is therefore populated when the notes substate runs.
 */
const primeApplicationState = {
  id: 'primeApplication',
  type: 'parallel',
  // initial: 'initialize',
  states: {
    primeDryChrome: {
      initial: 'initializing',
      states: {
        initializing: {
          entry: [
            /**
             * Starts the DryChromeMachine
             */
            assign((context, event) => {
              logger.info('Application is now ready to prime');
              logger.info('Application is now ready to prime', context, event);
              return {
                dryChromeMachineRef: spawn(DryChromeMachine, {
                  name: 'dryChromeMachine',
                }),
              };
            }),

            /*
             * Set dryChromeMachine initial state from parent
             */
            sendTo('dryChromeMachine', (context, event) => {
              logger.info('sendChromeModel');
              return {
                type: 'SetChromeModel',
                chromeModels: context?.chromeModels,
                breakpoint: context?.breakpoint,
              };
            }),
          ],
          always: {
            target: 'waitingForModel',
            // Transition immediately after spawning and sending the event
          },
        },
        waitingForModel: {
          on: {
            SetChromeModelFromChild: {
              target: 'complete',
              actions: assign((context, event) => {
                logger.info('SetChromeModelFromChild received, activeFlexLayoutModel set');
                return {
                  activeFlexLayoutModel: event.activeFlexLayoutModel,
                };
              }),
            },
          },
        },
        complete: {
          type: 'final',
        },
      },
      on: {
        SetModel: {
          actions: [
            (context, event) => {
              logger.info({ context, event }, 'SetModel from DryChromeClass');
            },
            sendTo('dryChromeMachine', (context, event) => {
              logger.info('SetModel');
              return {
                type: 'SetModel',
                data: {
                  model: event?.data?.model,
                  breakpoint: event?.data?.breakpoint,
                },
              };
            }),
          ],
        },
      },
    },
    primeAreaOfFocus: {
      id: 'primeAreaOfFocus',
      initial: 'setAreaOfFocus',
      states: {
        setAreaOfFocus: {
          /**
           * Set the area of focus to use based on last accessed
           */
          entry: ['setSelectAreaOfFocus'],
          always: [
            {
              target: 'primeNoteOperations',
              cond: (context) => {
                const { selectedAreaOfFocus, selectedAreaOfFocusId } = context;
                // No AOF configured — proceed with defaults
                if (!selectedAreaOfFocusId) return true;
                // AOF found — proceed
                if (Boolean(selectedAreaOfFocus?.area_of_focus_collection_id)) return true;
                // AOF data loaded (even if empty) — don't wait forever
                if (context.hasAreasOfFocusCollections) return true;
                return false;
              },
            },
          ],
        },
        /**
         * Parallel state: Run note selection and open note objects population in parallel
         * Both only need notes to be loaded, not each other
         */
        primeNoteOperations: {
          type: 'parallel',
          states: {
            primeSetSelectedNote: {
              /**
               * Select the last accessed note in the area of focus, or fallback to last note in order_of_items
               * This will be retried when notes are loaded via the retrySetSelectedNoteIfNeeded action
               */
              initial: 'setting',
              states: {
                setting: {
                  entry: ['setSelectedNote'],
                  always: [
                    {
                      target: 'complete',
                      cond: (context) => {
                        const selectedAreaOfFocus = context.selectedAreaOfFocus;
                        const selectedNoteIdFromStack =
                          selectedAreaOfFocus?.selected_stack?.[0];
                        const orderOfItems =
                          selectedAreaOfFocus?.order_of_items ?? [];
                        const lastNoteId =
                          orderOfItems.length > 0
                            ? orderOfItems[orderOfItems.length - 1]
                            : null;
                        const expectedNoteId =
                          selectedNoteIdFromStack || lastNoteId;

                        // No note configured in this area of focus — proceed
                        if (!expectedNoteId) return true;

                        // Note found and selected — proceed
                        if (context.notes && context.notes[expectedNoteId] && context.selectedNote) {
                          return true;
                        }

                        // Notes are loaded — proceed regardless (note may have been deleted)
                        if (context.notes && Object.keys(context.notes).length > 0) {
                          return true;
                        }

                        // Notes fetch completed (even if empty) — don't wait forever
                        if (context.hasNotes) return true;

                        return false;
                      },
                    },
                  ],
                },
                complete: {
                  type: 'final',
                },
              },
            },
            primeSetAreasOfFocusOpenNoteObjects: {
              /**
               * Get the list of open notes in the selected area of focus
               * Retry if notes aren't loaded yet
               * This runs in parallel with primeSetSelectedNote since it only needs notes, not selectedNote
               */
              initial: 'setting',
              states: {
                setting: {
                  entry: ['setAreasOfFocusOpenNoteObjects'],
                  always: [
                    {
                      target: 'complete',
                      cond: (context) => {
                        const orderOfItems =
                          context.selectedAreaOfFocus?.order_of_items ?? [];
                        const hasOrderOfItems = orderOfItems.length > 0;
                        const hasNotes =
                          context.notes &&
                          Object.keys(context.notes).length > 0;

                        // No notes in area of focus — proceed
                        if (!hasOrderOfItems) return true;
                        // Notes are loaded — proceed
                        if (hasNotes) return true;
                        // Notes fetch completed (even if empty) — don't wait forever
                        if (context.hasNotes) return true;

                        return false;
                      },
                    },
                  ],
                },
                complete: {
                  type: 'final',
                },
              },
            },
          },
          onDone: {
            target: 'primeSetSelectedNotesNotebook',
          },
        },
        primeSetSelectedNotesNotebook: {
          /**
           * Create the list of notes in the selected notebook
           * This must wait for both selectedNote and areaOfFocusOpenNoteObjects to be set
           */
          entry: ['setSelectedNotesNotebook'],
          type: 'final',
        },
      },
    },
    primeSelectedNotebook: {
      /**
       * Set the selected notebook based on the saved user settings
       */
      entry: ['setSelectedNotebook'],
      type: 'final',
    },
    primeNotes: {
      /**
       * Create a list of notes. selectedNote selection is handled by primeAreaOfFocus.
       */
      initial: 'createNotesList',
      states: {
        createNotesList: {
          entry: ['createNotesList', 'retrySetSelectedNoteIfNeeded'],
          always: 'complete',
        },
        complete: {
          type: 'final',
        },
      },
    },
    primeNotebooks: {
      /**
       * Create a Notebook tree
       */
      entry: ['createNotebookTree'],
      type: 'final',
    },
  },
};

export default primeApplicationState;
